/**
 * PortfolioInstancesView — a saved portfolio, read-only: its instances, who
 * made it, and what can be done with it.
 *
 * Legacy has this screen twice, nearly identical: the opened TELEMETRY
 * portfolio (views/Telemetry/DynamicTelemetryForm.vue :164-252) and the opened
 * CLOUD-CREDENTIAL one (views/CloudUsagesReport/ListOfInstances.vue). One
 * component here; each caller passes its columns and button labels.
 *
 * Wording verbatim: "List of Instances", "Created By:", CCA's "Policy Engine:",
 * EIA's Operational Safety Margin, the regions link, Delete, Cost / Instance
 * Advice. Loading the data and deleting are the caller's; this shows them.
 *
 * Fixed from legacy: the regions link names the CLOUD (legacy's telemetry
 * view said "List of AWS Regions" whatever the cloud).
 *
 * @param {{ key: string, header: string, numeric?: boolean, read: (row) => any }[]} columns
 * @param {() => void} [onAdvice] the Instance Advice button's click (the caller navigates)
 */
import { useMemo } from 'react';
import { useHref } from 'react-router-dom';

import { ArrowRightIcon, ChipIcon, ClearIcon, DeleteIcon } from '@components/common/icons';
import { Table } from '@components/common/Table';
import { SafetyMarginSlider } from '@components/eia/SafetyMarginSlider';
import { Button } from '@components/ui';
import { VariantGate } from '@variant/VariantGate';

import styles from './PortfolioInstancesView.module.css';

/** "Created By:" — the owner's address up to the @ (legacy createdBy.split('@')[0]). */
export function createdByName(user) {
  return String(user ?? '').split('@')[0];
}

/** A cell as shown: '—' for nothing (legacy showed blank or '-'). */
export function formatInstanceCell(value) {
  return value === null || value === undefined || String(value).trim() === '' ? '—' : String(value);
}

export function PortfolioInstancesView({
  portfolio,
  instances = [],
  columns,
  loading = false,
  policyEngine,
  cloud,
  headroom,
  onHeadroomChange,
  canDelete = false,
  canAdvice = false,
  canSeeRegions = false,
  busy = false,
  deleteLabel = 'Delete Portfolio',
  onDelete,
  onClose,
  /**
   * Instance Advice (CCA: Cost Advice) clicked — the caller opens the advice
   * page for this portfolio. Optional; without it the button does nothing.
   */
  onAdvice,
  /** Legacy's List of Instances also has a Cancel button doing what ✕ does. */
  showCancel = false,
}) {
  const regionsHref = useHref(`/regions-list?providerName=${encodeURIComponent(cloud ?? '')}`);

  // A key per row: the rows do not all carry an id (Datadog host_id,
  // CloudWatch instance_id, others nothing), so the position is used.
  const rows = useMemo(
    () => instances.map((row, i) => ({ ...row, _rowKey: `row-${i}` })),
    [instances],
  );
  const tableColumns = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        header: c.header,
        numeric: c.numeric,
        render: (row) => formatInstanceCell(c.read(row)),
      })),
    [columns],
  );

  return (
    <section className={styles.view} aria-labelledby="portfolio-instances-title">
      <header className={styles.header}>
        <div className={styles.heading}>
          <h2 id="portfolio-instances-title" className={styles.title}>
            List of Instances
          </h2>
          <p className={styles.meta}>
            <span>
              <strong>Created By:</strong> {createdByName(portfolio?.user)}
            </span>
            {policyEngine !== undefined ? (
              <VariantGate only="cca">
                <span>
                  <strong>Policy Engine:</strong> {policyEngine || '—'}
                </span>
              </VariantGate>
            ) : null}
            <span className={styles.name} title={portfolio?.name}>
              {portfolio?.name}
            </span>
          </p>
        </div>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close this portfolio"
          disabled={busy}
        >
          <ClearIcon />
        </button>
      </header>

      {/* EIA only, as legacy: the margin the advice is worked out with. */}
      {onHeadroomChange ? (
        <VariantGate only="eia">
          <SafetyMarginSlider value={headroom} onChange={onHeadroomChange} disabled={busy} />
        </VariantGate>
      ) : null}

      <Table
        caption="Portfolio instances"
        columns={tableColumns}
        rows={rows}
        rowKey={(row) => row._rowKey}
        loading={loading}
        emptyMessage="No instances found."
      />

      <div className={styles.footer}>
        {canSeeRegions && cloud ? (
          <a
            className={styles.regionsLink}
            href={regionsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            List of {cloud} Regions
            <ArrowRightIcon />
          </a>
        ) : null}
        <div className={styles.actions}>
          {showCancel ? (
            <Button
              variant="secondary-solid"
              className={styles.button}
              startIcon={<ClearIcon />}
              disabled={busy}
              onClick={onClose}
            >
              Cancel
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              variant="danger"
              className={styles.button}
              startIcon={<DeleteIcon />}
              disabled={busy || loading}
              onClick={onDelete}
            >
              {deleteLabel}
            </Button>
          ) : null}
          {canAdvice ? (
            // The caller's onAdvice opens the advice page — the same as Manage
            // Portfolio's button. This component knows no routes.
            <Button
              variant="secondary-solid"
              className={styles.button}
              startIcon={<ChipIcon />}
              disabled={busy || loading || rows.length === 0}
              title={rows.length === 0 ? 'The portfolio has no instances.' : undefined}
              onClick={onAdvice}
            >
              <VariantGate only="eia" fallback="Cost Advice">
                Instance Advice
              </VariantGate>
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default PortfolioInstancesView;
